import { ListType } from '@hyper-inferno/survey-elements-core';
import { ITransformationContext } from '../survey-engine/survey-engine.class';
import { cleanupSandbox, flattenResponses } from './piping-utils';

export class UniversalPiping {
  //   isNode: boolean;

  //   constructor(isNode: boolean) {
  //     this.isNode = isNode;
  //   }
  /**
   * Main entry point for text transformation
   */
  transform(text: string, ctx: ITransformationContext): string {
    if (!text || !/\{\{\s*.*?\s*\}\}/.test(text)) {
      return text;
    }

    const evalContext = this.createEvalContext(ctx);
    const regex = /\{\{\s*([^}]+)\s*\}\}/g;
    const matches = [...text.matchAll(regex)];

    let result = text;

    // Process all interpolations
    for (const [fullMatch, expression] of matches) {
      try {
        const evaluatedValue = this.execute(expression, evalContext);

        if (evaluatedValue === undefined) {
          throw new Error(`Variable undefined`);
        }

        // Replace and handle null/undefined as empty string
        result = result.replace(fullMatch, evaluatedValue ?? '');
      } catch (err) {
        console.error(`Piping error in expression [${expression}]:`, err);
        // On error, we return an empty string or the original tag based on preference
      }
    }

    return result;
  }

  /**
   * Prepares the sandboxed variables and helper functions
   */
  private createEvalContext(ctx: ITransformationContext) {
    // Access the engine instance injected into context
    const getListOptionsByName = (listName: string) => {
      const list = ctx.bundle.getListByListName(listName);

      if (!list) {
        throw new Error(`List not Found ${listName}`);
      } else if (list.list_type == ListType.NORMAL_LIST) {
        return list?.options ?? [];
      } else if (list.list_type == ListType.CONSTRUCTED_LIST) {
        return ctx.listBuilding.getOptionsByListId(list.id, ctx, false) ?? [];
      } else {
        throw new Error(`Unknown list type ${listName}`);
      }
    };

    // Helper 1: Get single label by position (Original listLabel)
    const listLabel = (listName: string, position: number) => {
      if (!getListOptionsByName)
        throw new Error('getListOptionsByName not found in context');
      const options = getListOptionsByName(listName);
      try {
        return options?.[position - 1]?.option_display ?? '';
      } catch {
        return '';
      }
    };

    // Helper 2: Get all labels as a quoted string array (Original listLabelsArray)
    const listLabelsArray = (listName: string) => {
      if (!getListOptionsByName)
        throw new Error('getListOptionsByName not found in context');
      const options = getListOptionsByName(listName);
      if (!options?.length) return '';
      return options.map((opt: any) => `"${opt.option_display}"`).join(', ');
    };

    // Helper 3: Format list with custom separators (Original displayListLabels)
    const displayListLabels = (
      listName: string,
      separator: string,
      finalSeparator: string,
    ) => {
      if (!getListOptionsByName)
        throw new Error('getListOptionsByName not found in context');
      const options = getListOptionsByName(listName);
      if (options.length === 0) return '';
      if (options.length === 1) return options[0].option_display;

      const allButLast = options
        .slice(0, -1)
        .map((opt: any) => opt.option_display)
        .join(separator);
      const last = options[options.length - 1].option_display;
      return `${allButLast}${` ${finalSeparator} `}${last}`;
    };

    // Helper 4: Get total session count (Original sessionCount)
    // const sessionCount =  () => {
    //   if (!engine) return 0;
    //   return saveState
    //     ?  ctx.bundle.fetchTotalSessionsQuery?.()
    //     : 0;
    //   // Note: Adjust based on where your sessionCount lives in your specific Repo
    // };

    // Environment-specific Assets
    // const MEDIA_PATH = this.isNode
    //   ? process.env['MEDIA_PATH']
    //   : (window as any).flutter_inappwebview
    //     ? 'https://local.device/assets/'
    //     : '/media/';
    const MEDIA_PATH = '/media/';

    const responses = cleanupSandbox(
      flattenResponses(ctx.session.jsn_responses),
    );

    // Return the flat object that will be exposed as local variables in the {{ }}
    return {
      ...responses, // Spread responses: {{ question_id }}
      session_id: ctx.session.id,
      survey_language: ctx.session.survey_language,
      MEDIA_PATH,
      SURVEY_ASSETS: MEDIA_PATH,
      listLabel,
      listLabelsArray,
      displayListLabels,
      //   sessionCount,
      console,
      JSON,
    };
  }

  /**
   * Executes the javascript string in an environment-safe sandbox
   */
  private execute(expression: string, sandbox: any): any {
    // if (this.isNode) {
    // eslint-disable-next-line no-constant-condition
    if (false) {
      //   const vm = require('node:vm');
      //   // Node uses the 'vm' module for better isolation
      //   const sandboxWithResult = { ...sandbox, __pipedResult__: '' };
      //   const context = vm.createContext(sandboxWithResult);
      //   // We wrap in an  IIFE to support '' inside the curly braces
      //   const script = new vm.Script(
      //     `( () => { __pipedResult__ = ${expression}; })()`,
      //   );
      //    script.runInContext(context);
      //   return sandboxWithResult.__pipedResult__;
    } else {
      // Browser uses 'new Function'

      //   console.log(sandbox);
      //   const keys = Object.keys(sandbox);
      //   const values = Object.values(sandbox);
      //   // Construct an  function where context keys are argument names
      //   const fn = new Function(
      //     ...keys,
      //     `"use strict"; return ( () => (${expression}))();`,
      //   );
      //   return fn(...values);
      // 1. We define ONE parameter called 'scope'
      // 2. We use 'with(scope)' so the expression can access properties as variables
      const fn = new Function(
        'scope',
        `
      with (scope) {
        return (() => {
          return ${expression};
        })();
      }
    `,
      );

      // 3. Use a Proxy to handle the 'with' block correctly
      // This allows the expression to find 'listLabel' inside the sandbox object
      const proxy = new Proxy(sandbox, {
        get: (target, prop) => {
          if (prop in target) return target[prop];
          return undefined;
        },
        // This is the magic part for 'with' blocks
        has: (target, prop) => {
          return true;
        },
      });

      return fn(proxy);
    }
  }
}
